import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, forkJoin } from 'rxjs';
import { NgbPaginationModule, NgbTooltipModule, NgbAlertModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { EditReferenceUserDataModalComponent } from '../edit-reference-user-data-modal/edit-reference-user-data-modal.component';
import {
  ReferenceUserDataService,
  ReferenceUserData,
  ReferenceUserDataFilters
} from '../../services/reference-user-data.service';

@Component({
  selector: 'app-reference-user-data-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbPaginationModule,
    NgbTooltipModule,
    NgbAlertModule,
    StaffHeaderComponent,
    StaffNavComponent
  ],
  templateUrl: './reference-user-data-management.component.html',
  styleUrl: './reference-user-data-management.component.scss'
})
export class ReferenceUserDataManagementComponent implements OnInit, OnDestroy {
  currentUser: AuthUser | null = null;
  isMobileMenuOpen = false;
  isDesktopSidebarCollapsed = false;

  // ข้อมูล
  allData: ReferenceUserData[] = [];
  filteredData: ReferenceUserData[] = [];
  isLoading = false;
  error: string | null = null;

  // สรุปยอด (การ์ดด้านบน)
  stats = {
    total: 0,
    registered: 0,
    notRegistered: 0,
    lifetime: 0,
    dated: 0,
    none: 0
  };

  // ตัวกรอง
  filters: ReferenceUserDataFilters = {
    searchTerm: '',
    idCardSearch: '',
    memberIdSearch: '',
    expiration: 'all',
    registration: 'all'
  };

  // การแบ่งหน้า
  page = 1;
  pageSize = 10;
  collectionSize = 0;

  // การเรียงลำดับ
  sortField = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  // ค่าจำนวนรายการต่อหน้า
  readonly pageSizeOptions = [10, 20, 50, 100];

  // เก็บ set ของ cid ที่สมัครแล้ว เพื่อใช้คำนวณสถานะซ้ำหลังแก้ไข
  private registeredCids = new Set<string>();

  private userSubscription: Subscription | undefined;
  private subscription = new Subscription();

  constructor(
    private referenceUserDataService: ReferenceUserDataService,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
      this.cdr.detectChanges();
    });

    this.ngZone.run(() => {
      setTimeout(() => {
        this.loadData();
      }, 100);
    });
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    this.subscription.unsubscribe();
  }

  // ===== Navigation / layout =====
  onLogout(): void {
    this.authService.logout();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  onDesktopSidebarToggle(isCollapsed: boolean): void {
    this.isDesktopSidebarCollapsed = isCollapsed;
  }

  onNavigate(route: string): void {
    this.closeMobileMenu();

    switch (route) {
      case 'reference-user-data':
        // อยู่บนหน้านี้อยู่แล้ว
        break;
      default:
        this.router.navigate(['/staff/dashboard']);
        break;
    }
  }

  // ===== Data loading =====
  loadData(): void {
    this.isLoading = true;
    this.error = null;

    // ดึงข้อมูลอ้างอิงทั้งหมด + รายชื่อผู้ใช้ที่สมัครแล้ว พร้อมกัน แล้วเทียบด้วย cid ฝั่ง client
    const sub = forkJoin({
      reference: this.referenceUserDataService.getReferenceUserData(1, 100000),
      registeredCids: this.referenceUserDataService.getRegisteredCidSet()
    }).subscribe({
      next: ({ reference, registeredCids }) => {
        this.ngZone.run(() => {
          const records = Array.isArray(reference?.data) ? reference.data : [];
          console.log('✅ โหลดข้อมูลอ้างอิงผู้ใช้งานสำเร็จ:', records.length, 'รายการ');

          // เก็บ set ไว้ใช้คำนวณซ้ำหลังแก้ไข
          this.registeredCids = registeredCids;

          // ทำเครื่องหมายว่าผู้ใช้รายใดสมัครเข้าระบบแล้ว (เทียบเลขบัตรกับตาราง users)
          this.allData = records.map((record) => ({
            ...record,
            hasRegistered: registeredCids.has(
              this.referenceUserDataService.normalizeIdNumber(record.idCardNumber)
            )
          }));

          // ===== Diagnostics: ช่วยตรวจว่าทำไม match / ไม่ match =====
          const matchedCount = this.allData.filter((r) => r.hasRegistered).length;
          const refSamples = this.allData
            .filter((r) => (r.idCardNumber || '').trim() !== '')
            .slice(0, 5)
            .map(
              (r) =>
                `"${r.idCardNumber}" -> "${this.referenceUserDataService.normalizeIdNumber(r.idCardNumber)}"`
            );
          console.log(
            `🔗 [match] reference=${this.allData.length} registeredCidSet=${registeredCids.size} matched=${matchedCount}`
          );
          console.log('🔎 [match] ตัวอย่างเลขบัตรใน reference (normalize แล้ว):', refSamples);
          if (registeredCids.size > 0 && matchedCount === 0) {
            console.warn(
              '⚠️ [match] มี cid ที่สมัครแล้วแต่จับคู่ไม่ได้เลย — เปรียบเทียบ sample ทั้งสองฝั่งด้านบนว่ารูปแบบตรงกันหรือไม่'
            );
          }

          this.computeStats();
          this.applyFilters();
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          console.error('❌ โหลดข้อมูลอ้างอิงผู้ใช้งานไม่สำเร็จ:', error);
          this.error = 'ไม่สามารถโหลดข้อมูลอ้างอิงผู้ใช้งานได้ กรุณาลองใหม่อีกครั้ง';
          this.allData = [];
          this.computeStats();
          this.applyFilters();
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      }
    });

    this.subscription.add(sub);
  }

  refreshData(): void {
    this.loadData();
  }

  private computeStats(): void {
    const total = this.allData.length;
    let lifetime = 0;
    let none = 0;
    let registered = 0;

    for (const record of this.allData) {
      const expiration = (record.expirationDate || '').trim();
      if (expiration === '') {
        none++;
      } else if (expiration.includes('ตลอดชีพ')) {
        lifetime++;
      }

      if (record.hasRegistered) {
        registered++;
      }
    }

    this.stats = {
      total,
      registered,
      notRegistered: total - registered,
      lifetime,
      none,
      dated: total - lifetime - none
    };
  }

  // ===== Filtering =====
  applyFilters(): void {
    const filtered = this.referenceUserDataService.filterData(this.allData, this.filters);
    this.filteredData = this.sortData(filtered);
    this.collectionSize = this.filteredData.length;

    // รีเซ็ตไปหน้าแรกเมื่อผลลัพธ์น้อยกว่าหน้าปัจจุบัน
    const maxPage = Math.max(1, Math.ceil(this.collectionSize / this.pageSize));
    if (this.page > maxPage) {
      this.page = 1;
    }
  }

  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      idCardSearch: '',
      memberIdSearch: '',
      expiration: 'all',
      registration: 'all'
    };
    this.page = 1;
    this.applyFilters();
  }

  setExpirationFilter(value: 'all' | 'lifetime' | 'dated' | 'none'): void {
    this.filters.expiration = value;
    this.page = 1;
    this.applyFilters();
  }

  setRegistrationFilter(value: 'all' | 'registered' | 'not-registered'): void {
    this.filters.registration = value;
    this.page = 1;
    this.applyFilters();
  }

  // ===== Sorting =====
  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.applyFilters();
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) {
      return '↕';
    }
    return this.sortDirection === 'asc' ? '↑' : '↓';
  }

  /** แปลงค่าเวลาเป็น timestamp (ms) สำหรับการเรียงลำดับ; ค่าที่ไม่ถูกต้อง = 0 (เก่าสุด) */
  private toTime(value: string | null | undefined): number {
    if (!value) {
      return 0;
    }
    const time = new Date(value).getTime();
    return isNaN(time) ? 0 : time;
  }

  private sortData(records: ReferenceUserData[]): ReferenceUserData[] {
    // ค่าเริ่มต้น: เรียงตาม updatedAt ล่าสุด -> เก่าสุดเสมอ
    if (!this.sortField) {
      return [...records].sort(
        (a, b) => this.toTime(b.updatedAt) - this.toTime(a.updatedAt)
      );
    }

    const field = this.sortField;
    const direction = this.sortDirection === 'asc' ? 1 : -1;

    return [...records].sort((a, b) => {
      let valueA = '';
      let valueB = '';

      switch (field) {
        case 'name':
          valueA = `${a.name || ''} ${a.lastname || ''}`;
          valueB = `${b.name || ''} ${b.lastname || ''}`;
          break;
        case 'idCardNumber':
          valueA = a.idCardNumber || '';
          valueB = b.idCardNumber || '';
          break;
        case 'idMember':
          valueA = a.idMember || '';
          valueB = b.idMember || '';
          break;
        case 'expirationDate':
          valueA = a.expirationDate || '';
          valueB = b.expirationDate || '';
          break;
        default:
          valueA = (a as any)[field] ?? '';
          valueB = (b as any)[field] ?? '';
          break;
      }

      return valueA.localeCompare(valueB, 'th') * direction;
    });
  }

  // ===== Pagination =====
  getCurrentPageData(): ReferenceUserData[] {
    const start = (this.page - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredData.slice(start, end);
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    this.page = 1;
  }

  trackByRecordId(index: number, record: ReferenceUserData): string {
    return record.id;
  }

  min(a: number, b: number): number {
    return Math.min(a, b);
  }

  // ===== Actions =====
  exportToCSV(): void {
    this.referenceUserDataService.exportToCSV(this.filteredData);
  }

  /**
   * เปิด Dialog เพิ่มข้อมูลอ้างอิงผู้ใช้งานรายการใหม่ (ใช้ฟอร์มเดียวกับการแก้ไข)
   */
  openCreateModal(): void {
    try {
      const modalRef = this.modalService.open(EditReferenceUserDataModalComponent, {
        size: 'lg',
        backdrop: 'static',
        keyboard: false,
        scrollable: true,
        centered: true
      });

      modalRef.componentInstance.mode = 'create';

      modalRef.result.then(
        (result: any) => {
          if (result && result.success && result.record) {
            this.applyCreatedRecord(result.record);
          }
        },
        () => {
          // ผู้ใช้กดยกเลิก / ปิด modal
        }
      );
    } catch (error) {
      console.error('❌ เปิด Dialog เพิ่มข้อมูลไม่สำเร็จ:', error);
      this.error = 'เกิดข้อผิดพลาดในการเปิดหน้าต่างเพิ่มข้อมูล';
    }
  }

  /**
   * เปิด Dialog แก้ไขข้อมูลอ้างอิงผู้ใช้งาน (แก้ไขได้ทุกฟิลด์ ยกเว้นเลขบัตรประชาชน)
   */
  openEditModal(record: ReferenceUserData, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    try {
      const modalRef = this.modalService.open(EditReferenceUserDataModalComponent, {
        size: 'lg',
        backdrop: 'static',
        keyboard: false,
        scrollable: true,
        centered: true
      });

      // ส่งข้อมูล (clone เพื่อไม่แก้ไขต้นฉบับก่อนบันทึกสำเร็จ)
      modalRef.componentInstance.recordData = { ...record };
      // ผู้ใช้ที่สมัครแล้ว -> เปิดแบบดูอย่างเดียว แก้ไขไม่ได้
      modalRef.componentInstance.readOnly = !!record.hasRegistered;

      modalRef.result.then(
        (result: any) => {
          if (result && result.success && result.record) {
            this.applyUpdatedRecord(result.record);
          }
        },
        () => {
          // ผู้ใช้กดยกเลิก / ปิด modal — ไม่ต้องทำอะไร
        }
      );
    } catch (error) {
      console.error('❌ เปิด Dialog แก้ไขไม่สำเร็จ:', error);
      this.error = 'เกิดข้อผิดพลาดในการเปิดหน้าต่างแก้ไขข้อมูล';
    }
  }

  /**
   * อัปเดตข้อมูลใน list หลังแก้ไขสำเร็จ (in-place) แล้วคำนวณสรุป/กรองใหม่
   */
  /**
   * เพิ่มรายการใหม่เข้า list (in-place) แล้วคำนวณสรุป/กรอง/เรียงลำดับใหม่
   */
  private applyCreatedRecord(created: ReferenceUserData): void {
    const hasRegistered = this.registeredCids.has(
      this.referenceUserDataService.normalizeIdNumber(created.idCardNumber)
    );
    this.allData = [{ ...created, hasRegistered }, ...this.allData];
    this.page = 1; // ไปหน้าแรกเพื่อให้เห็นรายการใหม่ (เรียงตาม updatedAt ล่าสุด)
    this.computeStats();
    this.applyFilters();
    this.cdr.detectChanges();
  }

  private applyUpdatedRecord(updated: ReferenceUserData): void {
    const index = this.allData.findIndex(item => item.id === updated.id);
    if (index !== -1) {
      // เลขบัตรอาจถูกแก้ไข -> คำนวณสถานะการสมัครใหม่
      const hasRegistered = this.registeredCids.has(
        this.referenceUserDataService.normalizeIdNumber(updated.idCardNumber)
      );
      this.allData[index] = { ...this.allData[index], ...updated, hasRegistered };
    }
    this.computeStats();
    this.applyFilters();
    this.cdr.detectChanges();
  }

  getFullName(record: ReferenceUserData): string {
    return `${record.title || ''}${record.name || ''} ${record.lastname || ''}`.trim();
  }

  getExpirationBadgeClass(record: ReferenceUserData): string {
    const expiration = (record.expirationDate || '').trim();
    if (expiration === '') {
      return 'bg-secondary';
    }
    if (expiration.includes('ตลอดชีพ')) {
      return 'bg-success';
    }
    return 'bg-info';
  }
}
